<template>
	<div class="ori-logo" :class="{ 'ori-logo--no-animation': noAnimation }" :style="customStyle">
		<div class="ori-logo__symbol">
			<img :src="logoUrl" :alt="logoAlt" class="ori-logo__image" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStaticAssets } from "@/composables/useStaticAssets";

// Props
interface Props {
	/** Tamanho pequeno (32px) - ignorado se size for especificado */
	small?: boolean;
	/** Tamanho médio (76px) - ignorado se size for especificado */
	medium?: boolean;
	/** Variante do logo a ser exibida */
	variant?: "full" | "icon" | "text";
	/** Tamanho personalizado - sobrescreve small e medium */
	size?: number | string;
	/** Desabilitar animação de entrada */
	noAnimation?: boolean;
	/** Cor do logo - aplica filtro CSS para alterar a cor */
	color?:
		| "default"
		| "white"
		| "primary"
		| "secondary"
		| "accent"
		| "dark"
		| "light";
}

const props = withDefaults(defineProps<Props>(), {
	small: false,
	medium: false,
	variant: "full",
	size: undefined,
	noAnimation: false,
	color: "default",
});

// Composables
const { getLogoInfo } = useStaticAssets();

// Computed
const logoInfo = computed(() => getLogoInfo(props.variant));

const logoUrl = computed(() => logoInfo.value.url);
const logoAlt = computed(() => {
	const baseAlt = logoInfo.value.alt;
	let sizeText = "";

	if (props.size) {
		const sizeValue =
			typeof props.size === "number" ? `${props.size}px` : props.size;
		sizeText = ` (${sizeValue})`;
	} else if (props.small) {
		sizeText = " (Pequeno)";
	} else if (props.medium) {
		sizeText = " (Médio)";
	}

	const colorText = props.color !== "default" ? ` - ${props.color}` : "";

	return `${baseAlt}${sizeText}${colorText}`;
});

// Computed para determinar o tamanho final
const finalSize = computed(() => {
	if (props.size) {
		return typeof props.size === "number" ? `${props.size}px` : props.size;
	} else if (props.small) {
		return "32px";
	} else if (props.medium) {
		return "76px";
	}
	return "80px"; // tamanho padrão
});

// Computed para determinar o filtro de cor
const colorFilter = computed(() => {
	switch (props.color) {
		case "white":
			return "brightness(0) invert(1)"; // Converte para branco
		case "primary":
			return "brightness(0) saturate(100%) invert(49%) sepia(69%) saturate(1000%) hue-rotate(15deg) brightness(103%) contrast(95%)"; // Cor primária
		case "secondary":
			return "brightness(0) saturate(100%) invert(44%) sepia(63%) saturate(1000%) hue-rotate(180deg) brightness(99%) contrast(101%)"; // Cor secundária
		case "accent":
			return "brightness(0) saturate(100%) invert(73%) sepia(89%) saturate(1000%) hue-rotate(315deg) brightness(104%) contrast(97%)"; // Cor de destaque
		case "dark":
			return "brightness(0) saturate(100%) invert(13%) sepia(7%) saturate(1000%) hue-rotate(169deg) brightness(95%) contrast(86%)"; // Escuro
		case "light":
			return "brightness(0) invert(1) opacity(0.8)"; // Claro
		case "default":
		default:
			return "none"; // Cor original
	}
});

// Computed para o estilo customizado
const customStyle = computed(() => {
	return {
		"--logo-size": finalSize.value,
		"--logo-filter": colorFilter.value,
	};
});
</script>

<style lang="scss" scoped>
@use "@/styles/_variables.scss" as vars;

.ori-logo {
	display: flex;
	align-items: center;
	animation: fadeIn 1.2s ease-in-out;

	&--no-animation {
		animation: none;
	}

	&__symbol {
		position: relative;
		width: var(--logo-size);
		height: var(--logo-size);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__image {
		width: 100%;
		height: 100%;
		object-fit: contain;
		filter: var(--logo-filter);
		transition: filter 0.3s ease;
	}
}
</style>
